import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MapPin, Clock, Phone, Youtube } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "../../context/StoreContext";
import { api, errMsg } from "../../lib/api";
import { Logo } from "./Logo";
import { NAV } from "./Header";
import { Switch } from "../ui/switch";

const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.6 2.6 0 0 1-2.6-2.6 2.6 2.6 0 0 1 3.37-2.48V9.66a5.68 5.68 0 0 0-.77-.05A5.7 5.7 0 0 0 4.17 15.3 5.7 5.7 0 0 0 9.86 21a5.7 5.7 0 0 0 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.25-1.48" /></svg>
);

function ConsentToggle() {
  const { customer, setCustomer, forgetDevice } = useStore();
  if (!customer) return null;
  const change = async (v) => {
    try {
      await api.put("/customers/me/consent", { promo_consent: v });
      setCustomer({ ...customer, promo_consent: v });
      toast.success(v ? "Você passará a receber nossas ofertas." : "Você não receberá mais mensagens promocionais.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3 text-xs text-[#A1A1AA]">
      <p>Acesso: {customer.phone_masked}</p>
      <label className="flex items-center gap-3">
        <Switch checked={customer.promo_consent} onCheckedChange={change} data-testid="footer-consent-switch" />
        Receber ofertas pelo WhatsApp
      </label>
      <button onClick={forgetDevice} className="underline underline-offset-2 hover:text-[#F3E5AB]" data-testid="footer-forget-device">Sair deste dispositivo</button>
    </div>
  );
}

export default function Footer() {
  const { settings: s } = useStore();
  const socials = [
    { key: "instagram", Icon: Instagram }, { key: "facebook", Icon: Facebook },
    { key: "tiktok", Icon: TikTok }, { key: "youtube", Icon: Youtube },
  ].filter((x) => s?.[x.key]);
  const contacts = [
    { v: s?.contact_phone, Icon: Phone }, { v: s?.contact_email, Icon: Mail },
    { v: s?.address, Icon: MapPin }, { v: s?.business_hours, Icon: Clock },
  ].filter((x) => x.v);

  return (
    <footer className="arabesque relative mt-24 border-t border-[#D4AF37]/15 bg-[#08080A]" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 md:grid-cols-4 lg:px-8">
        <div className="space-y-5 md:col-span-1">
          <Logo settings={s} />
          {socials.length > 0 && (
            <div className="flex gap-3">
              {socials.map(({ key, Icon }) => (
                <a key={key} href={s[key]} target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D4AF37]/25 text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10" aria-label={key} data-testid={`footer-social-${key}`}>
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="eyebrow mb-5">Navegação</p>
          <ul className="space-y-3 text-sm">
            {NAV.map((n) => <li key={n.label}><Link to={n.to} className="text-[#A1A1AA] transition-colors hover:text-[#F3E5AB]">{n.label}</Link></li>)}
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-5">Contato</p>
          <ul className="space-y-3 text-sm text-[#A1A1AA]">
            {contacts.map(({ v, Icon }) => <li key={v} className="flex gap-3"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />{v}</li>)}
            {contacts.length === 0 && <li>Atendimento pelo WhatsApp.</li>}
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-5">Privacidade</p>
          <Link to="/privacidade" className="mb-4 block text-sm text-[#A1A1AA] hover:text-[#F3E5AB]" data-testid="footer-privacy-link">Política de privacidade</Link>
          <ConsentToggle />
        </div>
      </div>
      <div className="border-t border-white/5 py-6 text-center text-xs text-[#A1A1AA]/70">
        © {new Date().getFullYear()} {s?.store_name || "EMAIA PERFUMES"}. Todos os direitos reservados.
      </div>
    </footer>
  );
}
